import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Wand2,
  ArrowRight,
  Check,
  Loader2,
  Printer,
  Palette,
  BookOpen,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Clock,
  Utensils,
  Heart,
  Gift,
} from "lucide-react";
import {
  claimFreeBuddy,
  createCustomBuddy,
  createSimpleDigitalPackCheckout,
  isTestMode,
  redeemTesterCode,
} from "../lib/api";
import useFreeBuddyCode from "../lib/useFreeBuddyCode";
import useSeo from "../lib/useSeo";
import { trackEvent, trackOnce } from "../lib/analytics";
import RecentBuddiesMarquee from "../components/RecentBuddiesMarquee";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// ---- SINGLE-OFFER validation landing page.
// Route: /create-your-plate-buddy
// One goal: generate a Buddy → pay $5 → print at home. That's it.
//
// Structure (locked):
//   1. Hero (H1 + subhead + primary CTA)
//   2. Real-family social proof marquee
//   3. Three creation steps ("Design → Print → Play")
//   4. Generator (real /api/custom-buddy call)
//   5. Single $5 pack card + real Stripe checkout
//   6. Trust bar (guarantee, delivery timing, care)
//   7. Short FAQ

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PRICE = 5;
const GEN_STATUS_LABEL = {
  drawing: "Drawing your Buddy\u2026",
  checking: "Double-checking every detail\u2026",
  redrawing: "Redrawing for a better match\u2026",
};

const STEPS = [
  {
    n: "01",
    icon: Wand2,
    title: "Design",
    body: "Give us a name and a few words about the food. We draw your one-of-a-kind Buddy in about 30 seconds.",
    color: "#F26A34",
  },
  {
    n: "02",
    icon: Printer,
    title: "Print",
    body: "Two PDFs land in your inbox — a full-color character card and a ready-to-color coloring page.",
    color: "#7CC5D4",
  },
  {
    n: "03",
    icon: Heart,
    title: "Play",
    body: "Slip it under a clear plate at dinner. The plate becomes a little theatre — and the food has a stage.",
    color: "#FFC15E",
  },
];

// Brand-sheet verbatim 4-step flow. Shown only on the "brand" A/B variant so
// we can test the fuller "Dream it up → Meet → Print and play → Make it a ritual"
// narrative against the current 3-step "Design → Print → Play".
const BRAND_STEPS = [
  {
    n: "01",
    icon: Sparkles,
    title: "Dream it up",
    body: "Your child describes their Buddy. Silly, spooky, sparkly: anything goes.",
    color: "#4E2C80", // Deep Purple — leads
  },
  {
    n: "02",
    icon: Wand2,
    title: "Meet the Buddy",
    body: "We bring their character to life as a one-of-a-kind creation.",
    color: "#06A5A1", // Buddy Teal — supports
  },
  {
    n: "03",
    icon: Printer,
    title: "Print and play",
    body: "Slide the Buddy under a clear plate and let the adventure begin.",
    color: "#F26A34", // Tongue Orange — accent
  },
  {
    n: "04",
    icon: Heart,
    title: "Make it a ritual",
    body: "New Buddies, coloring sheets, and family cooking fun keep the world growing.",
    color: "#4E2C80",
  },
];

const PACK_INCLUDES = [
  { icon: Palette, label: "One-of-a-kind character card (PDF)" },
  { icon: BookOpen, label: "Ready-to-color coloring page (PDF)" },
  { icon: Sparkles, label: "Both files land in your inbox instantly" },
  { icon: Printer, label: "Print at home or any print shop" },
];

const FAQ = [
  {
    q: "How is the Buddy made?",
    a: "You describe them in a few words. A character generator we built (powered by Google's Gemini) draws them for you in about 30 seconds. If it's not quite right, you can regenerate — free — before paying.",
  },
  {
    q: "What do I get for $5?",
    a: "Two printable PDFs delivered to your inbox — a full-color character card of your Buddy, and a black-and-white coloring page of the same Buddy for your kid to color in. Print at home or any print shop.",
  },
  {
    q: "How do I use it at dinner?",
    a: "Print the character card, slide it under a clear glass plate, and serve dinner on top. The plate becomes a little theatre. The coloring page keeps small hands busy while you cook.",
  },
  {
    q: "What if my kid doesn't love their Buddy?",
    a: "30-day happiness guarantee. Email hi@platebuddies.com and we'll redesign it or refund — no forms, no fights.",
  },
];

export default function CreateYourPlateBuddy() {
  useSeo({
    title: "Design a custom Plate Buddy &mdash; $5 printable PDF | Plate Buddies",
    description:
      "Design a one-of-a-kind food Buddy in 30 seconds. Print it, slide it under a clear plate, and dinner suddenly has a friend. Instant PDF, $5. Ages 2&ndash;7.",
    url: "https://platebuddies.com/",
  });

  const navigate = useNavigate();

  // Read the Homepage A/B hero variant key so a user who saw the "brand"
  // (brand-guide verbatim) hero on / continues to see brand-sheet copy here.
  // If they came straight to /create we still assign one so the funnel never
  // mixes voices mid-visit.
  const [heroVariant, setHeroVariant] = useState("family");
  useEffect(() => {
    try {
      let v = sessionStorage.getItem("platebuddies_home_hero_copy_variant");
      if (v === "picky") v = "brand"; // migrate deprecated key
      if (!v || !["family", "brand"].includes(v)) {
        v = Math.random() < 0.5 ? "family" : "brand";
        sessionStorage.setItem("platebuddies_home_hero_copy_variant", v);
      } else {
        sessionStorage.setItem("platebuddies_home_hero_copy_variant", v);
      }
      setHeroVariant(v);
      trackOnce("create_hero_copy_view", { variant: v });
    } catch {
      setHeroVariant("brand");
    }
  }, []);
  const isBrand = heroVariant === "brand";

  // GA4 events fire once per session (page view + view_item on the offer).
  useEffect(() => {
    window.scrollTo(0, 0);
    trackOnce("simple_offer_view", { landing: "create-your-plate-buddy" });
    trackOnce("view_item", {
      currency: "USD",
      value: PRICE,
      items: [
        {
          item_id: "simple_digital_pack",
          item_name: "Printable Buddy Pack",
          price: PRICE,
          quantity: 1,
        },
      ],
    });
  }, []);

  // Generator form state
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [email, setEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(null);
  const [buddy, setBuddy] = useState(null);

  const [checkingOut, setCheckingOut] = useState(false);
  const [openFaqIdx, setOpenFaqIdx] = useState(null);
  const generatorRef = useRef(null);

  // Tester bypass code
  const [testMode] = useState(isTestMode);
  const [testerCode, setTesterCode] = useState("");
  const [testerBusy, setTesterBusy] = useState(false);
  const [testerRedeemed, setTesterRedeemed] = useState(false);

  // Free Buddy QR code (?free=CODE) — one free printable pack per email
  const { code: freeCode, forget: forgetFreeCode, clear: clearFreeCode } = useFreeBuddyCode();
  const [freeOptIn, setFreeOptIn] = useState(false);
  const [freeBusy, setFreeBusy] = useState(false);
  const [freeClaimed, setFreeClaimed] = useState(false);
  const freeMode = Boolean(freeCode) && !testMode;

  useEffect(() => {
    if (!freeCode) return;
    trackOnce("free_buddy_code_landing", { code: freeCode });
    setTimeout(() => generatorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [freeCode]);

  const canGenerate = name.trim().length >= 3 && prompt.trim().length >= 3 && !generating;

  const scrollToGenerator = () => {
    trackEvent("simple_offer_cta_click", { where: "hero" });
    generatorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const generate = async (e) => {
    if (e) e.preventDefault();
    if (!canGenerate) return;
    setGenerating(true);
    try {
      trackEvent("simple_offer_generate_click", { name: name.trim() });
      const created = await createCustomBuddy(
        {
          name: name.trim(),
          prompt: prompt.trim(),
        },
        (job) => setGenStatus(job.status),
      );
      setBuddy(created);
      setTesterRedeemed(false);
      if (freeClaimed) {
        clearFreeCode();
        setFreeClaimed(false);
      }
      trackEvent("simple_offer_buddy_ready", { buddy_id: created?.id });
      trackEvent("add_to_cart", {
        currency: "USD",
        value: PRICE,
        items: [
          {
            item_id: "simple_digital_pack",
            item_name: "Printable Buddy Pack",
            price: PRICE,
            quantity: 1,
            custom_buddy_id: created?.id,
            custom_buddy_name: created?.name,
          },
        ],
      });
      setTimeout(() => {
        document.getElementById("buddy-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      trackEvent("simple_offer_generate_error");
      toast.error(
        err?.response?.data?.detail ||
          "Something went sideways generating your Buddy. Give it another try?",
      );
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  };

  const checkout = async (where) => {
    if (!buddy) {
      // Scroll them up to the generator instead.
      scrollToGenerator();
      return;
    }
    const cleanedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleanedEmail)) {
      toast.error("Add a valid email so we can send you your Buddy's PDFs.");
      document.getElementById("input-email")?.focus();
      return;
    }
    setCheckingOut(true);
    try {
      trackEvent("simple_offer_buy_click", { buddy_id: buddy.id, where });
      trackEvent("begin_checkout", {
        currency: "USD",
        value: PRICE,
        items: [
          {
            item_id: "simple_digital_pack",
            item_name: "Printable Buddy Pack",
            price: PRICE,
            quantity: 1,
            custom_buddy_id: buddy.id,
            custom_buddy_name: buddy.name,
          },
        ],
      });
      const { url, session_id } = await createSimpleDigitalPackCheckout({
        custom_buddy_id: buddy.id,
        email: cleanedEmail,
        origin_url: window.location.origin,
      });
      trackEvent("simple_offer_checkout_created", { session_id });
      // Use replace so the Stripe page isn't a new history entry —
      // hitting back from Stripe should return here, not to any earlier page.
      if (url) window.location.replace(url);
    } catch (err) {
      trackEvent("simple_offer_checkout_error");
      toast.error(err?.response?.data?.detail || "Couldn't start checkout — try once more?");
      setCheckingOut(false);
    }
  };

  const redeem = async () => {
    if (!buddy) {
      toast.error("Design your Buddy first, then redeem.");
      scrollToGenerator();
      return;
    }
    const cleanedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleanedEmail)) {
      toast.error("Add a valid email so we can send your Buddy's PDFs.");
      document.getElementById("input-email")?.focus();
      return;
    }
    const code = testerCode.trim().toUpperCase();
    if (!testMode && code.length < 3) {
      toast.error("Enter your tester code first.");
      return;
    }
    setTesterBusy(true);
    try {
      const res = await redeemTesterCode({
        code: testMode ? undefined : code,
        custom_buddy_id: buddy.id,
        email: cleanedEmail,
      });
      trackEvent("simple_offer_tester_redeem", {
        buddy_id: buddy.id,
        remaining_uses: res?.remaining_uses,
      });
      setTesterRedeemed(true);
      toast.success(res?.message || "PDFs on their way!");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't redeem that code — try once more?"
      );
    } finally {
      setTesterBusy(false);
    }
  };

  const buddyResultBg = useMemo(() => buddy?.bg || "#FFF6D6", [buddy]);

  const claimFree = async () => {
    if (!buddy) {
      scrollToGenerator();
      return;
    }
    const cleanedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleanedEmail)) {
      toast.error("Add a valid email so we can send your free Buddy's PDFs.");
      document.getElementById("input-email")?.focus();
      return;
    }
    setFreeBusy(true);
    try {
      const res = await claimFreeBuddy({
        code: freeCode,
        custom_buddy_id: buddy.id,
        email: cleanedEmail,
        join_list: freeOptIn,
      });
      trackEvent("free_buddy_claimed", { buddy_id: buddy.id, joined_list: freeOptIn });
      setFreeClaimed(true);
      forgetFreeCode();
      toast.success(res?.message || "Your free prints are on their way!");
    } catch (err) {
      if (err?.response?.status === 409) clearFreeCode();
      toast.error(err?.response?.data?.detail || "Couldn't claim your free Buddy. Try once more?");
    } finally {
      setFreeBusy(false);
    }
  };

  // JSON-LD structured data — three schemas in one payload:
  //   1. WebSite  — helps Google's knowledge panel form the "Plate Buddies" entity.
  //   2. Organization  — brand entity with logo + sameAs handles.
  //   3. FAQPage  — matches the "Questions parents actually ask" section
  //                 verbatim so Google can serve rich FAQ snippets in SERP.
  //
  // Kept in one <script type="application/ld+json"> so Google sees them as
  // an @graph — the recommended shape for multi-entity pages.
  const structuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": "https://platebuddies.com/#website",
          name: "Plate Buddies",
          url: "https://platebuddies.com/",
          publisher: { "@id": "https://platebuddies.com/#organization" },
          inLanguage: "en-US",
        },
        {
          "@type": "Organization",
          "@id": "https://platebuddies.com/#organization",
          name: "Plate Buddies",
          url: "https://platebuddies.com/",
          logo: {
            "@type": "ImageObject",
            url: "https://platebuddies.com/brand/logo-wordmark.png",
            width: 512,
            height: 512,
          },
          sameAs: [
            "https://www.instagram.com/plate_buddies",
            "https://www.tiktok.com/@platebuddies",
          ],
          slogan: "A Buddy for every plate — ages 2–7.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.a,
            },
          })),
        },
      ],
    }),
    []
  );

  return (
    <div className="font-body min-h-screen bg-pb-cream">
      {/* JSON-LD: WebSite + Organization + FAQPage — rich-result SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      {/* Full brand nav — matches the rest of the site. */}
      <Navbar />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 pb-32 sm:pb-20">
        {/* 1. Hero */}
        <section data-testid="section-headline" className="text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-pb-brand-teal inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Play. Explore. Connect. · Ages 2–7
          </p>
          <h1
            data-testid="simple-headline"
            className="font-display font-black text-pb-brand-ink mt-4 text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl mx-auto"
          >
            Design a Buddy for tonight&rsquo;s dinner.
          </h1>
          <p className="mt-5 text-lg text-pb-brand-ink/70 max-w-2xl mx-auto leading-relaxed">
            Your child dreams up a character. We turn it into a printable Buddy that sits under their plate &mdash; and turns dinner into an adventure.
          </p>
          <button
            type="button"
            data-testid="btn-hero-cta"
            onClick={scrollToGenerator}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-pb-brand-orange text-white font-display font-black text-base sm:text-lg px-8 py-4 hover:scale-105 transition"
          >
            <Wand2 className="h-5 w-5" /> Create a Buddy
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-4 text-xs sm:text-sm text-pb-brand-ink/70">
            Free to try. 30 seconds. No account needed. Love it?{" "}
            <b className="text-pb-brand-orange">Download the printable pack for $5.</b>
          </p>
        </section>

        {/* 1b. The 3-frame product story — Buddy → Print → Plate.
            Clean brand layout: three white-framed panels with Buddy Teal
            chevrons between them. Replaces the pink-splatter Etsy composite. */}
        <section data-testid="section-plate-demo" className="mt-14 sm:mt-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-pb-brand-teal">
                How it works
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl mt-2 text-pb-brand-ink">
                Create &rarr; Print &rarr; Plate.
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-5 sm:gap-3">
              {/* Step 1 — Phone */}
              <figure data-testid="plate-demo-phone" className="rounded-[2rem] overflow-hidden bg-white border-[6px] border-white aspect-[4/5]">
                <img src="/etsy/step-phone.jpg" alt="A kid-friendly Buddy character shown on a phone screen." loading="lazy" className="w-full h-full object-cover" />
              </figure>
              {/* Chevron — Buddy Teal brand accent */}
              <div aria-hidden className="hidden sm:flex items-center justify-center text-pb-brand-teal">
                <ChevronRight className="h-8 w-8" strokeWidth={3} />
              </div>
              {/* Step 2 — Print */}
              <figure data-testid="plate-demo-print" className="rounded-[2rem] overflow-hidden bg-white border-[6px] border-white aspect-[4/5]">
                <img src="/etsy/step-print.jpg" alt="A hand holding the printed Buddy character on cardstock." loading="lazy" className="w-full h-full object-cover" />
              </figure>
              <div aria-hidden className="hidden sm:flex items-center justify-center text-pb-brand-teal">
                <ChevronRight className="h-8 w-8" strokeWidth={3} />
              </div>
              {/* Step 3 — Plate */}
              <figure data-testid="plate-demo-plate" className="rounded-[2rem] overflow-hidden bg-white border-[6px] border-white aspect-[4/5]">
                <img src="/etsy/step-plate.jpg" alt="The printed Buddy under a clear glass plate with real broccoli, chicken, and rice on top." loading="lazy" className="w-full h-full object-cover" />
              </figure>
            </div>

            <div className="mt-8 grid sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-start gap-5 sm:gap-3 text-center">
              <div>
                <p className="font-display font-black text-pb-brand-ink text-lg">1. Free Buddy</p>
                <p className="text-sm text-pb-brand-ink/65 mt-1">Describe your Buddy. We draw it in 30 seconds — no card needed.</p>
              </div>
              <div className="hidden sm:block" aria-hidden />
              <div>
                <p className="font-display font-black text-pb-brand-ink text-lg">2. Print at home</p>
                <p className="text-sm text-pb-brand-ink/65 mt-1">Love it? $5 unlocks the color card + coloring page as instant PDFs.</p>
              </div>
              <div className="hidden sm:block" aria-hidden />
              <div>
                <p className="font-display font-black text-pb-brand-ink text-lg">3. Dinner has a friend</p>
                <p className="text-sm text-pb-brand-ink/65 mt-1">Slide the print under a clear plate. Serve dinner on top. Kids notice.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Real families' Buddies — auto-scrolling marquee */}
        <div data-testid="section-example" className="-mx-5 sm:-mx-8 mt-14">
          <RecentBuddiesMarquee
            theme="cream"
            limit={24}
            headline="Meet the Buddies real families made this week."
            subline="Every one of these was dreamed up by a kid. Yours will be one-of-a-kind too."
          />
        </div>

        {/* 3. Three steps */}
        <section data-testid="section-steps" className="mt-16">
          <p className={isBrand ? "text-xs font-black uppercase tracking-widest text-pb-brand-purple text-center" : "text-xs font-black uppercase tracking-widest text-pb-coral text-center"}>
            How it works
          </p>
          <h2 className={isBrand ? "mt-3 font-brand-display font-black text-3xl md:text-4xl text-pb-brand-ink text-center leading-tight" : "mt-3 font-display font-black text-3xl md:text-4xl text-pb-ink text-center leading-tight"}>
            {isBrand ? "Dream. Meet. Print. Make it a ritual." : "Design. Print. Play. In under a minute."}
          </h2>
          <div className={isBrand ? "mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5" : "mt-10 grid md:grid-cols-3 gap-5"}>
            {(isBrand ? BRAND_STEPS : STEPS).map((s) => (
              <div
                key={s.n}
                className="bg-white rounded-3xl p-7 shadow-[0_14px_36px_rgba(28,22,40,0.06)] hover:shadow-[0_20px_48px_rgba(28,22,40,0.1)] transition"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex h-11 w-11 rounded-2xl items-center justify-center text-white shadow-sm"
                    style={{ background: s.color }}
                  >
                    <s.icon className="h-5 w-5" strokeWidth={2.5} />
                  </span>
                  <span className={isBrand ? "text-[11px] font-black uppercase tracking-widest text-pb-brand-ink/50" : "text-[11px] font-black uppercase tracking-widest text-pb-soft"}>
                    Step {s.n}
                  </span>
                </div>
                <h3 className={isBrand ? "mt-4 font-brand-display font-black text-2xl text-pb-brand-ink leading-tight" : "mt-4 font-display font-black text-2xl text-pb-ink leading-tight"}>
                  {s.title}
                </h3>
                <p className={isBrand ? "mt-2 text-sm font-brand-body text-pb-brand-ink/65 leading-relaxed" : "mt-2 text-sm text-pb-soft leading-relaxed"}>{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 4. Generator — cream module sitting on a Deep Purple gradient band */}
        <div className="relative mt-16 -mx-5 sm:-mx-8 px-5 sm:px-8 py-10 md:py-14 rounded-[2.5rem] overflow-clip"
          style={{ background: "linear-gradient(135deg, #4E2C80 0%, #2B1655 55%, #1C1628 100%)" }}
        >
          <div aria-hidden className="pointer-events-none absolute -top-20 -right-16 h-72 w-72 rounded-full bg-pb-coral/20 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-pb-aqua/15 blur-3xl" />
          <section
            id="generator"
            ref={generatorRef}
            data-testid="section-generator"
            className="relative z-10 rounded-[2rem] bg-pb-cream p-7 md:p-10 shadow-[0_30px_60px_rgba(28,22,40,0.35)] border border-white scroll-mt-24"
          >
          <p className="text-xs font-black uppercase tracking-widest text-pb-coral">
            Your Buddy
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl text-pb-ink leading-tight">
            Give us a name and a doodle-in-words.
          </h2>
          {!freeMode && (
            <p className="mt-2 text-pb-soft text-sm">
              Free to design. If you love how it turned out, the PDFs are <b className="text-pb-ink">$5</b>.
            </p>
          )}
          {testMode && <TestModeNote />}
          {freeMode && <FreeCodeBanner />}
          <form onSubmit={generate} className="mt-6 grid sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-1">
              <span className="text-[11px] font-black uppercase tracking-widest text-pb-soft">
                Buddy&rsquo;s name
              </span>
              <input
                data-testid="input-buddy-name"
                type="text"
                required
                maxLength={40}
                placeholder="e.g. Broccoli Boomer"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-white bg-white focus:border-pb-coral outline-none font-semibold text-pb-ink placeholder:text-pb-soft/60"
              />
            </label>
            <label className="block sm:col-span-1">
              <span className="text-[11px] font-black uppercase tracking-widest text-pb-soft">
                {isBrand ? "Where should we send your Buddy?" : "Your email"}
              </span>
              <input
                id="input-email"
                data-testid="input-email"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-white bg-white focus:border-pb-coral outline-none font-semibold text-pb-ink placeholder:text-pb-soft/60"
              />
              {isBrand && (
                <span className="mt-1.5 block text-[11px] font-brand-body text-pb-brand-ink/55 leading-snug">
                  We&rsquo;ll email a link to save your child&rsquo;s creation, plus the printable pack when you&rsquo;re ready. No spam &mdash; just their Buddy, safely kept.
                </span>
              )}
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[11px] font-black uppercase tracking-widest text-pb-soft">
                Describe them (any food, any personality)
              </span>
              <textarea
                data-testid="input-prompt"
                required
                rows={3}
                maxLength={200}
                placeholder="A brave green broccoli with a knight's helmet who loves solving mysteries"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-white bg-white focus:border-pb-coral outline-none font-semibold text-pb-ink placeholder:text-pb-soft/60 resize-none"
              />
            </label>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={!canGenerate}
                data-testid="btn-generate"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-black text-base px-8 py-4 shadow-[0_12px_28px_rgba(242,106,52,0.4)] hover:bg-pb-plum hover:shadow-[0_12px_28px_rgba(78,44,128,0.4)] hover:scale-[1.02] transition disabled:bg-pb-ink/40 disabled:text-white/90 disabled:shadow-none disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> {GEN_STATUS_LABEL[genStatus] || GEN_STATUS_LABEL.drawing}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" /> Create my Buddy
                  </>
                )}
              </button>
              {!freeMode && (
                <p className="mt-3 text-[11px] text-pb-soft text-center">
                  Design free — <b className="text-pb-coral">$5 unlocks both printables</b>. No card until then.
                </p>
              )}
            </div>
          </form>
          </section>
        </div>

          {/* Result reveal */}
          {buddy && (
            <div
              id="buddy-result"
              data-testid="buddy-result"
              className="mt-10 rounded-[2rem] p-6 md:p-10 border-2 border-dashed border-pb-coral/40 relative z-10 scroll-mt-8"
              style={{ background: buddyResultBg }}
            >
              <p className="text-xs font-black uppercase tracking-widest text-pb-coral">
                Meet your Buddy
              </p>
              <div className="mt-3 grid md:grid-cols-2 gap-8 items-center">
                <div className="relative max-w-sm mx-auto">
                  <div className="relative overflow-hidden rounded-[2rem] shadow-[0_20px_50px_rgba(28,22,40,0.15)] border-[5px] border-white -rotate-[2deg]">
                    <img
                      src={buddy.image}
                      alt={buddy.name}
                      data-testid="buddy-image"
                      className="w-full block"
                    />
                    {/* Real diagonal preview watermark — stripped at checkout */}
                    <div
                      aria-hidden="true"
                      data-testid="buddy-watermark"
                      className="absolute inset-0 pointer-events-none select-none flex items-center justify-center"
                    >
                      <div
                        className="whitespace-nowrap font-display font-black text-white/70 text-[54px] leading-[1.1] tracking-[0.35em] mix-blend-overlay drop-shadow-[0_2px_6px_rgba(28,22,40,0.35)]"
                        style={{ transform: "rotate(-24deg)" }}
                      >
                        PREVIEW · PREVIEW
                        <br />
                        PLATE&nbsp;BUDDIES
                        <br />
                        PREVIEW · PREVIEW
                      </div>
                    </div>
                  </div>
                  <span className="absolute -top-3 -right-3 sm:-right-6 bg-white rounded-full px-3 py-1 shadow-lg rotate-3 text-[9px] font-black uppercase tracking-widest text-pb-coral border-2 border-pb-coral/30 z-10">
                    Preview
                  </span>
                </div>
                <div>
                  <h3
                    className="font-display font-black text-4xl md:text-5xl text-pb-ink leading-[1.05]"
                    style={{ color: buddy.color }}
                  >
                    {buddy.name}
                  </h3>
                  {buddy.tagline && (
                    <p className="mt-2 text-xs font-black uppercase tracking-widest text-pb-ink/70">
                      {buddy.tagline}
                    </p>
                  )}
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    {testMode ? (
                      <button
                        type="button"
                        onClick={redeem}
                        disabled={testerBusy || testerRedeemed}
                        data-testid="btn-send-test-file"
                        className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-black text-sm sm:text-base px-6 py-3 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-105 transition disabled:opacity-60 disabled:hover:scale-100"
                      >
                        {testerBusy ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                        ) : testerRedeemed ? (
                          <><Check className="h-4 w-4" /> Sent, check your inbox</>
                        ) : (
                          <><Printer className="h-4 w-4" /> Send free test file</>
                        )}
                      </button>
                    ) : freeMode ? (
                      <FreeClaimButton busy={freeBusy} claimed={freeClaimed} onClick={claimFree} />
                    ) : (
                    <button
                      type="button"
                      onClick={() => checkout("buddy-reveal")}
                      disabled={checkingOut}
                      data-testid="btn-buy-inline"
                      className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-black text-sm sm:text-base px-6 py-3 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-105 transition disabled:opacity-60 disabled:hover:scale-100"
                    >
                      {checkingOut ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Redirecting...
                        </>
                      ) : (
                        <>
                          <Printer className="h-4 w-4" /> Get My Prints · $5
                        </>
                      )}
                    </button>
                    )}
                    <button
                      type="button"
                      onClick={generate}
                      disabled={generating}
                      data-testid="btn-regenerate"
                      className="text-xs font-black uppercase tracking-widest text-pb-ink/60 hover:text-pb-coral transition inline-flex items-center gap-1.5"
                    >
                      <Loader2 className={`h-3 w-3 ${generating ? "animate-spin" : ""}`} /> Not quite? Regenerate free
                    </button>
                  </div>
                  {freeMode && !freeClaimed && <FreeClaimOptIn checked={freeOptIn} onChange={setFreeOptIn} />}
                </div>
              </div>
            </div>
          )}

        {/* 5. (removed) The standalone $5 pack card was redundant — the
            buddy-preview card above already surfaces the Stripe checkout CTA
            once the user has generated a Buddy. */}

        {/* 6. Trust bar */}
        <section
          data-testid="section-trust"
          className="mt-14 grid sm:grid-cols-3 gap-4"
        >
          <TrustItem
            icon={Clock}
            title="Files in your inbox"
            body={freeMode ? "PDFs land in your inbox the moment you claim. Refresh, print, done." : "PDFs land in your inbox the moment checkout closes. Refresh, print, done."}
          />
          <TrustItem
            icon={Utensils}
            title="Made for real dinners"
            body="Built for kids ages 2&ndash;7 &mdash; the classic &lsquo;won&rsquo;t eat what&rsquo;s on the plate&rsquo; years."
          />
          <TrustItem
            icon={Heart}
            title={freeMode ? "Happiness guaranteed" : "$5 back if it flops"}
            body="30-day happiness guarantee. Email hi@platebuddies.com and we&rsquo;ll make it right."
          />
        </section>

        {/* 7. FAQ */}
        <section data-testid="section-faq" className="mt-16">
          <p className="text-xs font-black uppercase tracking-widest text-pb-coral text-center">
            A few we hear
          </p>
          <h2 className="mt-3 font-display font-black text-3xl text-pb-ink text-center">
            Questions parents actually ask.
          </h2>
          <div className="mt-8 max-w-3xl mx-auto space-y-3">
            {FAQ.map((item, i) => {
              const open = openFaqIdx === i;
              return (
                <div
                  key={i}
                  data-testid={`faq-item-${i}`}
                  className="bg-white rounded-2xl border border-pb-cream overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpenFaqIdx(open ? null : i);
                      if (!open) trackEvent("simple_offer_faq_open", { q: item.q });
                    }}
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                  >
                    <span className="font-display font-black text-pb-ink text-base sm:text-lg pr-4">
                      {item.q}
                    </span>
                    {open ? (
                      <ChevronUp className="h-4 w-4 text-pb-soft shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-pb-soft shrink-0" />
                    )}
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-sm text-pb-ink/80 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* Sticky mobile CTA — persistent throughout scroll on <sm */}
      <div
        data-testid="sticky-mobile-cta"
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-pb-cream shadow-[0_-6px_20px_rgba(28,22,40,0.06)]"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex flex-col">
            <span className="text-[10px] font-black uppercase tracking-widest text-pb-soft leading-none">
              Printable Buddy
            </span>
            <span className="font-display font-black text-2xl text-pb-ink leading-none mt-1">
              {testMode || freeMode ? "Free" : "$5"}
            </span>
          </div>
          <button
            type="button"
            onClick={() =>
              buddy
                ? testMode
                  ? redeem()
                  : freeMode
                    ? claimFree()
                    : checkout("sticky-mobile")
                : scrollToGenerator()
            }
            disabled={
              checkingOut || testerBusy || freeBusy || (testMode && testerRedeemed) || (freeMode && freeClaimed)
            }
            data-testid="btn-sticky-mobile-cta"
            className="ml-auto inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-black px-5 py-3 shadow-[0_10px_24px_rgba(242,106,52,0.4)] disabled:opacity-60"
          >
            {checkingOut ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Redirecting...
              </>
            ) : buddy ? (
              <>
                <Printer className="h-4 w-4" />{" "}
                {testMode ? "Free test file" : freeMode ? (freeClaimed ? "Sent" : "Get free prints") : "Unlock Prints · $5"}
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" /> Free Preview
              </>
            )}
          </button>
        </div>
      </div>

      {/* Minimal legal footer */}
      <footer className="border-t border-pb-ink/5 py-6 pb-24 sm:pb-6">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-pb-soft">
          <span>© {new Date().getFullYear()} Plate Buddies · Made in the US</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-pb-coral">Privacy</Link>
            <Link to="/terms" className="hover:text-pb-coral">Terms</Link>
            <Link to="/refunds" className="hover:text-pb-coral">Refunds</Link>
            <a href="mailto:hi@platebuddies.com" className="hover:text-pb-coral">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FreeCodeBanner() {
  return (
    <p
      data-testid="free-code-banner"
      className="mt-4 flex items-start gap-2 rounded-2xl bg-pb-coral/10 border border-pb-coral/30 px-4 py-3 text-sm font-bold text-pb-ink leading-snug"
    >
      <Gift className="h-4 w-4 shrink-0 mt-0.5 text-pb-coral" />
      <span>Your free Buddy is unlocked! Design it below and the printables are on us. One free Buddy per email.</span>
    </p>
  );
}

function FreeClaimButton({ busy, claimed, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || claimed}
      data-testid="btn-claim-free"
      className="inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-black text-sm sm:text-base px-6 py-3 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-105 transition disabled:opacity-60 disabled:hover:scale-100"
    >
      {busy ? (
        <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
      ) : claimed ? (
        <><Check className="h-4 w-4" /> Sent, check your inbox</>
      ) : (
        <><Gift className="h-4 w-4" /> Get my free prints</>
      )}
    </button>
  );
}

function FreeClaimOptIn({ checked, onChange }) {
  return (
    <label className="mt-4 flex items-start gap-2.5 max-w-sm cursor-pointer text-xs font-semibold text-pb-ink/70 leading-snug">
      <input
        type="checkbox"
        data-testid="free-claim-optin"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[#F26A34]"
      />
      Also send me Plate Buddies recipes, new Buddies and launch news.
    </label>
  );
}

function TestModeNote() {
  return (
    <p
      data-testid="test-mode-note"
      className="mt-4 inline-flex items-start gap-2 rounded-2xl bg-pb-brand-purple/10 border border-pb-brand-purple/25 px-4 py-2.5 text-xs font-bold text-pb-brand-purple leading-snug"
    >
      <ShieldCheck className="h-4 w-4 shrink-0 mt-px" />
      Test mode is on (you&rsquo;re signed in to Admin). Unlimited Buddies, free files, and nothing you make shows in the public gallery.
    </p>
  );
}

function TrustItem({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
      <span className="h-10 w-10 rounded-2xl bg-pb-cream inline-flex items-center justify-center">
        <Icon className="h-4 w-4 text-pb-coral" />
      </span>
      <h3 className="mt-3 font-display font-black text-lg text-pb-ink">{title}</h3>
      <p className="mt-1 text-sm text-pb-soft leading-relaxed">{body}</p>
    </div>
  );
}
